// frontend/src/tabs/useDirtyTabs.js
//
// Lightweight registry for tabs that have unsaved edits. Editable grids
// (useEditableGrid) and pages with a page-level guard (useUnsavedChangesGuard)
// report through useDirtyTabReporter: dirty while edits are unsaved, clean on
// save / cancel. The TabStrip reads isDirty(tabId)
// before closing a tab to show the "Discard unsaved changes?" prompt;
// ReloadOnNewVersion reads anyTabDirty() before reloading the document.
//
// State lives in a module-level Set rather than a Context to keep the
// registry global to all consumers without re-rendering the whole
// TabsProvider on every dirty-flag flip. Subscribers (the close button
// guard) call into the imperative API on click — no per-render reads.

import { useContext, useEffect } from 'react';
import { TabIdContext } from './tabsContexts';

const dirtyTabs = new Set();
const listeners = new Set();

function notify() {
  listeners.forEach((fn) => fn());
}

export function markDirty(tabId) {
  if (!tabId) return;
  if (!dirtyTabs.has(tabId)) {
    dirtyTabs.add(tabId);
    notify();
  }
}

export function markClean(tabId) {
  if (!tabId) return;
  if (dirtyTabs.has(tabId)) {
    dirtyTabs.delete(tabId);
    notify();
  }
}

export function isDirty(tabId) {
  return dirtyTabs.has(tabId);
}

export function anyTabDirty() {
  return dirtyTabs.size > 0;
}

export function subscribe(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

// Hook-shaped wrapper kept thin: consumers import { markDirty, markClean }
// directly when they only need to call them on lifecycle events. The hook
// is for components that need to render based on dirty state (the close
// button confirmation gate).
export function useDirtyTabs() {
  return { markDirty, markClean, isDirty, subscribe };
}

// Drop-in hook for editable surfaces (the natural fit is inside
// useEditableGridState). Mirrors the page's `isDirty` flag into the
// registry, keyed by the tab id from TabIdContext. No-op when called
// outside the tabbed shell (e.g. in unit tests that render the page
// without a TabIdContext provider).
//
// No cleanup function: this hook only mirrors the flag in. The registry
// entry is removed by the reducer's CLOSE action, when the tab closes.
export function useDirtyTabReporter(isDirty) {
  const tabId = useContext(TabIdContext);
  useEffect(() => {
    if (!tabId) return;
    if (isDirty) markDirty(tabId);
    else markClean(tabId);
  }, [tabId, isDirty]);
}
